import { ENTETE_UTILISATEUR_ATTENDU, type ReponseConnexion, type UtilisateurPublic } from '@rh/shared';
import { oublierFiltres } from './filtres-session';
import { oublierParcours } from './parcours';
import { noterHeureServeur } from './horsligne/horloge';
import { VERROU_RENOUVELLEMENT, verrouNavigateur, type Verrou } from './horsligne/verrou';

/**
 * Le jeton d'accès reste en mémoire : ni localStorage ni sessionStorage, pour
 * qu'une faille XSS ne puisse pas l'emporter. Le rafraîchissement vit dans un
 * cookie httpOnly que le navigateur envoie seul. Le compte et l'échéance du
 * jeton l'accompagnent, en mémoire aussi.
 */
let jetonAcces: string | null = null;
let utilisateurId: string | null = null;
let jetonExpireLe = 0;

export const MESSAGE_RESEAU = 'Réseau indisponible : nouvel essai au retour de la connexion.';
export const MESSAGE_REPONSE_ILLISIBLE = 'Réponse inattendue du réseau (portail Wi-Fi ?)';
const MESSAGE_DELAI = 'Délai de réponse dépassé';
const MESSAGE_SESSION_NON_RENOUVELEE = 'Session non renouvelée : réseau indisponible.';
const MESSAGE_AUTRE_COMPTE = 'Session ouverte sous un autre compte : envoi suspendu.';
const MESSAGE_DECONNEXION_EN_ATTENTE = 'Déconnexion en cours sur ce téléphone : reconnectez-vous.';

const BASE = '/api/v1';
/** Seule trace de session sur le disque : une déconnexion demandée, pas encore reçue par le serveur. */
const CLE_DECONNEXION = 'rh-app:deconnexion-en-attente';
const DELAI_RENOUVELLEMENT_MS = 15_000;
const DELAI_DECONNEXION_MS = 10_000;
const DELAI_CONNEXION_MS = 20_000;
/** Intermédiaire ou serveur débordé : la demande n'a pas été jugée, elle se retente. */
const STATUTS_PANNE = new Set([408, 425, 429, 502, 503, 504]);
/** Routes publiques d'authentification : un 401 y est une réponse, pas un jeton expiré. */
const ROUTES_SANS_RENOUVELLEMENT = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

/** Refus jugé par l'API : réponse JSON portant un `message`, et un `code` pour les refus codés. */
export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    readonly code: string | null = null,
    readonly details: unknown = undefined,
    readonly corps: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

/**
 * Tout ce qui n'est pas un refus de l'API : réseau coupé, délai dépassé, portail
 * Wi-Fi, passerelle en panne. La même demande pourra être refaite telle quelle.
 */
export class ErreurReseau extends Error {
  constructor(
    message: string = MESSAGE_RESEAU,
    readonly statut: number | null = null,
  ) {
    super(message);
    this.name = 'ErreurReseau';
  }
}

/** Le jeton en mémoire n'est pas celui du compte pour lequel la demande part : rien n'est envoyé. */
export class ErreurAutreCompte extends Error {
  constructor(message: string = MESSAGE_AUTRE_COMPTE) {
    super(message);
    this.name = 'ErreurAutreCompte';
  }
}

export interface OptionsAppel extends RequestInit {
  /** Interne : empêche une boucle si le rafraîchissement échoue lui aussi. */
  dejaRafraichi?: boolean;
  /** Pose x-rh-utilisateur ; refuse d'envoyer (ErreurAutreCompte) si le jeton n'est pas celui de ce compte. */
  pourUtilisateur?: string;
  /** Délai de l'aller-retour complet, lecture du corps comprise. */
  delaiMs?: number;
}

export type IssueRenouvellement =
  | { etat: 'renouvele'; reponse: ReponseConnexion }
  | { etat: 'hors_ligne' }
  | { etat: 'refuse'; message: string; code: string | null };

export type EvenementSession =
  | { type: 'renouvelee'; utilisateur: UtilisateurPublic }
  | { type: 'perdue'; message: string; code: string | null; utilisateurId: string | null }
  | { type: 'autre_utilisateur'; utilisateurId: string };

export type EtatReseau = 'en_ligne' | 'hors_ligne';

// ---------------------------------------------------------------------------
// Événements : session et réseau
// ---------------------------------------------------------------------------

const ecouteursSession = new Set<(evenement: EvenementSession) => void>();
const ecouteursReseau = new Set<(etat: EtatReseau) => void>();
let etatReseau: EtatReseau = 'en_ligne';

/** Un écouteur en faute ne doit ni priver les suivants ni changer l'issue d'un appel. */
function publier<E>(ecouteurs: ReadonlySet<(evenement: E) => void>, evenement: E): void {
  for (const ecouteur of [...ecouteurs]) {
    try {
      ecouteur(evenement);
    } catch (erreur) {
      console.error(erreur);
    }
  }
}

/** Publié au changement seulement : toute réponse prouve le réseau, tout échec de transport le met en doute. */
function noterReseau(etat: EtatReseau): void {
  if (etat === etatReseau) return;
  etatReseau = etat;
  publier(ecouteursReseau, etat);
}

export function ecouterSession(ecouteur: (evenement: EvenementSession) => void): () => void {
  ecouteursSession.add(ecouteur);
  return () => {
    ecouteursSession.delete(ecouteur);
  };
}

export function ecouterReseau(ecouteur: (etat: EtatReseau) => void): () => void {
  ecouteursReseau.add(ecouteur);
  return () => {
    ecouteursReseau.delete(ecouteur);
  };
}

export function dernierEtatReseau(): EtatReseau {
  return etatReseau;
}

// ---------------------------------------------------------------------------
// Session en mémoire
// ---------------------------------------------------------------------------

/** Tests : pose un jeton seul, sans compte ni échéance. */
export function memoriserJeton(jeton: string | null): void {
  jetonAcces = jeton;
  if (jeton === null) {
    utilisateurId = null;
    jetonExpireLe = 0;
  }
}

export function memoriserSession(reponse: ReponseConnexion | null): void {
  if (!reponse) {
    memoriserJeton(null);
    return;
  }
  jetonAcces = reponse.jetonAcces;
  utilisateurId = reponse.utilisateur.id;
  jetonExpireLe = Date.now() + reponse.expireDans * 1000;
  sessionPerdue = null;
  deconnexionVolontaire = false;
}

/**
 * Fin de session vue par cet onglet, jusqu'à la prochaine session ouverte. Les
 * brouillons s'y règlent : une session refusée par l'API garde la frappe en
 * cours (reconnexion attendue), une déconnexion volontaire ou un compte
 * désactivé ne laissent plus rien entrer sur le téléphone.
 */
let sessionPerdue: { utilisateurId: string; code: string | null } | null = null;
let deconnexionVolontaire = false;

/** Session fermée dans un autre onglet (message `session-fermee`) : jeton oublié, motif retenu. */
export function oublierSession(motif: 'deconnexion' | 'perdue', code: string | null = null): void {
  const compte = utilisateurDuJeton() ?? sessionPerdue?.utilisateurId ?? null;
  memoriserSession(null);
  // Les filtres de liste et le parcours des fiches ne vivent que le temps
  // d'une session (demandes du 2026-09-19).
  oublierFiltres();
  oublierParcours();
  if (motif === 'deconnexion') {
    deconnexionVolontaire = true;
    sessionPerdue = null;
  } else if (compte) {
    sessionPerdue = { utilisateurId: compte, code };
  }
}

/**
 * Un brouillon de ce compte peut-il encore s'écrire sur le téléphone ? Oui sous
 * sa session, et juste après un refus de l'API (session révoquée ou expirée :
 * la saisie attend la reconnexion). Non sous un autre compte, pendant ou après
 * une déconnexion volontaire, ni sur `COMPTE_INACTIF` (saisies effacées).
 */
export function brouillonPermis(compte: string): boolean {
  if (deconnexionVolontaire || deconnexionEnAttente()) return false;
  if (jetonAcces) return utilisateurId === compte;
  return sessionPerdue !== null && sessionPerdue.utilisateurId === compte && sessionPerdue.code !== 'COMPTE_INACTIF';
}

/** Compte du jeton en mémoire, null sans jeton. */
export function utilisateurDuJeton(): string | null {
  return jetonAcces ? utilisateurId : null;
}

/** Millisecondes avant l'expiration du jeton en mémoire (0 sans jeton ou s'il a expiré). */
export function jetonExpireDans(): number {
  return jetonAcces ? Math.max(0, jetonExpireLe - Date.now()) : 0;
}

function reponseConnexionValide(corps: unknown): corps is ReponseConnexion {
  if (typeof corps !== 'object' || corps === null) return false;
  const { jetonAcces: jeton, expireDans, utilisateur } = corps as Record<string, unknown>;
  return (
    typeof jeton === 'string' &&
    jeton !== '' &&
    typeof expireDans === 'number' &&
    Number.isFinite(expireDans) &&
    expireDans > 0 &&
    typeof utilisateur === 'object' &&
    utilisateur !== null &&
    typeof (utilisateur as { id?: unknown }).id === 'string' &&
    (utilisateur as { id: string }).id !== ''
  );
}

// ---------------------------------------------------------------------------
// Marque de déconnexion (localStorage, jamais de jeton)
// ---------------------------------------------------------------------------

export function deconnexionEnAttente(): boolean {
  try {
    return globalThis.localStorage?.getItem(CLE_DECONNEXION) === '1';
  } catch {
    return false;
  }
}

function poserMarqueDeconnexion(): void {
  try {
    globalThis.localStorage?.setItem(CLE_DECONNEXION, '1');
  } catch {
    /* stockage refusé : la déconnexion se fait au mieux, sans reprise après relance */
  }
}

function effacerMarqueDeconnexion(): void {
  try {
    globalThis.localStorage?.removeItem(CLE_DECONNEXION);
  } catch {
    /* stockage refusé : rien à effacer */
  }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

interface OptionsEchange {
  delaiMs?: number;
  signal?: AbortSignal | null;
}

const estErreurClassee = (erreur: unknown) =>
  erreur instanceof ErreurApi || erreur instanceof ErreurReseau || erreur instanceof ErreurAutreCompte;

/**
 * Un aller-retour complet, lecture du corps comprise, sous le délai demandé :
 * une connexion qui se fige au milieu du corps ne bloque pas plus qu'une autre.
 * Un rejet du transport devient `ErreurReseau`, sauf l'abandon voulu par
 * l'appelant, relancé tel quel. Toute réponse corrige l'horloge du téléphone.
 */
async function echanger<T>(
  url: string,
  init: RequestInit,
  { delaiMs, signal }: OptionsEchange,
  lire: (reponse: Response) => Promise<T>,
): Promise<T> {
  const controleur = new AbortController();
  let depasse = false;
  const relayer = () => controleur.abort(signal?.reason);
  if (signal?.aborted) relayer();
  else signal?.addEventListener('abort', relayer, { once: true });
  const minuteur =
    delaiMs === undefined
      ? undefined
      : setTimeout(() => {
          depasse = true;
          controleur.abort();
        }, delaiMs);

  const echecTransport = (erreur: unknown): unknown => {
    if (signal?.aborted && !depasse) return erreur;
    noterReseau('hors_ligne');
    return new ErreurReseau(depasse ? MESSAGE_DELAI : MESSAGE_RESEAU);
  };

  try {
    const envoyeLe = Date.now();
    let reponse: Response;
    try {
      reponse = await fetch(url, { ...init, signal: controleur.signal });
    } catch (erreur) {
      throw echecTransport(erreur);
    }
    noterReseau('en_ligne');
    noterHeureServeur(reponse.headers.get('Date'), envoyeLe, Date.now());
    try {
      return await lire(reponse);
    } catch (erreur) {
      throw estErreurClassee(erreur) ? erreur : echecTransport(erreur);
    }
  } finally {
    clearTimeout(minuteur);
    signal?.removeEventListener('abort', relayer);
  }
}

/** Corps JSON, ou undefined s'il est vide ou n'est pas du JSON. */
async function lireJson(reponse: Response): Promise<unknown> {
  const texte = await reponse.text();
  if (texte.trim() === '') return undefined;
  try {
    return JSON.parse(texte);
  } catch {
    return undefined;
  }
}

function messageDe(valeur: unknown): string | null {
  if (typeof valeur === 'string' && valeur !== '') return valeur;
  if (Array.isArray(valeur) && valeur.length > 0 && valeur.every((m) => typeof m === 'string')) {
    return valeur.join(', ');
  }
  return null;
}

/**
 * Un refus n'est retenu comme tel que s'il vient de l'API : du JSON avec un
 * message. Une page de passerelle, un portail Wi-Fi ou un statut d'encombrement
 * sont des pannes, que la file retentera.
 */
async function erreurDepuisReponse(reponse: Response): Promise<ErreurApi | ErreurReseau> {
  if (STATUTS_PANNE.has(reponse.status)) {
    await reponse.body?.cancel().catch(() => undefined);
    return new ErreurReseau(MESSAGE_RESEAU, reponse.status);
  }
  const corps = await lireJson(reponse);
  const objet =
    typeof corps === 'object' && corps !== null && !Array.isArray(corps) ? (corps as Record<string, unknown>) : null;
  const message = objet ? messageDe(objet.message) : null;
  if (!objet || message === null) return new ErreurReseau(MESSAGE_REPONSE_ILLISIBLE, reponse.status);
  return new ErreurApi(message, reponse.status, typeof objet.code === 'string' ? objet.code : null, objet.details, objet);
}

/** Sans jeton, renouvelle d'abord ; puis vérifie que le jeton est bien celui du compte attendu. */
async function verifierCompte(pour: string): Promise<void> {
  if (!jetonAcces) {
    const issue = await renouveler();
    if (issue.etat === 'hors_ligne') throw new ErreurReseau(MESSAGE_SESSION_NON_RENOUVELEE);
    if (issue.etat === 'refuse') throw new ErreurApi(issue.message, 401, issue.code);
  }
  if (utilisateurDuJeton() !== pour) throw new ErreurAutreCompte();
}

type IssueEchange<T> =
  | { genre: 'valeur'; valeur: T }
  | { genre: 'a_renouveler'; erreur: ErreurApi | ErreurReseau };

/**
 * Coeur des appels : pose le jeton, renouvelle une fois sur 401, rejoue une
 * fois. `lire` tire la valeur d'une réponse 2xx (JSON, blob…) ; toute autre
 * réponse devient une erreur classée.
 */
async function requete<T>(chemin: string, options: OptionsAppel, lire: (reponse: Response) => Promise<T>): Promise<T> {
  const { dejaRafraichi, pourUtilisateur, delaiMs, headers, signal, ...reste } = options;
  if (pourUtilisateur !== undefined) await verifierCompte(pourUtilisateur);
  const utilisateurAvant = utilisateurDuJeton();
  const renouvelable = !dejaRafraichi && !ROUTES_SANS_RENOUVELLEMENT.has(chemin);

  const issue = await echanger<IssueEchange<T>>(
    `${BASE}${chemin}`,
    {
      ...reste,
      credentials: 'same-origin',
      headers: {
        // Un envoi de fichiers laisse le navigateur poser l'en-tête multipart et sa frontière.
        ...(reste.body && !(reste.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(jetonAcces ? { Authorization: `Bearer ${jetonAcces}` } : {}),
        // Vérifié par la garde de l'API : une saisie ne part jamais sous un autre compte.
        ...(pourUtilisateur !== undefined ? { [ENTETE_UTILISATEUR_ATTENDU]: pourUtilisateur } : {}),
        ...headers,
      },
    },
    { delaiMs, signal },
    async (reponse): Promise<IssueEchange<T>> => {
      if (reponse.ok) return { genre: 'valeur', valeur: await lire(reponse) };
      const erreur = await erreurDepuisReponse(reponse);
      // Jeton expiré (15 min) : renouveler, puis rejouer une fois.
      if (reponse.status === 401 && renouvelable) return { genre: 'a_renouveler', erreur };
      throw erreur;
    },
  );
  if (issue.genre === 'valeur') return issue.valeur;

  const renouvellement = await renouveler();
  if (renouvellement.etat === 'hors_ligne') throw new ErreurReseau(MESSAGE_SESSION_NON_RENOUVELEE);
  if (renouvellement.etat === 'refuse') throw issue.erreur;
  const nouveau = renouvellement.reponse.utilisateur.id;
  if (
    (pourUtilisateur !== undefined && nouveau !== pourUtilisateur) ||
    (utilisateurAvant !== null && nouveau !== utilisateurAvant)
  ) {
    // Le cookie a été repris par un autre compte (autre onglet) : ne rien rejouer sous son nom.
    throw new ErreurAutreCompte();
  }
  return requete(chemin, { ...options, dejaRafraichi: true }, lire);
}

export function appel<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  return requete(chemin, options, async (reponse) => {
    if (reponse.status === 204) return undefined as T;
    const texte = await reponse.text();
    if (texte === '') return undefined as T;
    try {
      return JSON.parse(texte) as T;
    } catch {
      // 200 en HTML : typiquement la page d'un portail Wi-Fi, pas l'API.
      throw new ErreurReseau(MESSAGE_REPONSE_ILLISIBLE, reponse.status);
    }
  });
}

/**
 * Documents : ils ne sortent de l'API qu'avec le jeton, qu'un `src` d'image ou
 * d'iframe ne sait pas transmettre. On récupère donc le binaire puis on crée
 * une URL locale, à révoquer quand l'aperçu se ferme.
 */
export async function chargerDocument(id: string): Promise<string> {
  const contenu = await requete(`/documents/${id}`, {}, (reponse) => reponse.blob());
  return URL.createObjectURL(contenu);
}

/** Contenu brut d'un document, pour un rendu dans la page (aperçu PDF). */
export function chargerBinaire(id: string): Promise<ArrayBuffer> {
  return requete(`/documents/${id}`, {}, (reponse) => reponse.arrayBuffer());
}

/** Fichier produit par l'API (export Excel), avec le nom annoncé par `Content-Disposition`. */
export function chargerFichier(chemin: string): Promise<{ contenu: Blob; nomFichier: string | null }> {
  return requete(chemin, {}, async (reponse) => ({
    contenu: await reponse.blob(),
    nomFichier: /filename="([^"]+)"/.exec(reponse.headers.get('Content-Disposition') ?? '')?.[1] ?? null,
  }));
}

// ---------------------------------------------------------------------------
// Connexion, renouvellement, déconnexion
// ---------------------------------------------------------------------------

let verrou: Verrou | null = null;
let renouvellementEnCours: Promise<IssueRenouvellement> | null = null;
let deconnexionEnCours: Promise<'faite' | 'en_attente'> | null = null;
/** Compte les déconnexions : un renouvellement qui en chevauche une est écarté. */
let deconnexions = 0;

/** Tests : remplace le verrou entre onglets. */
export function utiliserVerrou(v: Verrou): void {
  verrou = v;
}

export async function connexion(email: string, motDePasse: string): Promise<ReponseConnexion> {
  // Une déconnexion restée en attente se termine d'abord, au mieux : l'ancienne
  // session ne doit pas survivre sur le serveur à l'ouverture de la nouvelle.
  if (deconnexionEnAttente()) await (deconnexionEnCours ?? fermerSessionServeur());
  const reponse = await appel<ReponseConnexion>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, motDePasse }),
    dejaRafraichi: true,
    delaiMs: DELAI_CONNEXION_MS,
  });
  if (!reponseConnexionValide(reponse)) throw new ErreurReseau(MESSAGE_REPONSE_ILLISIBLE);
  effacerMarqueDeconnexion();
  memoriserSession(reponse);
  return reponse;
}

/**
 * Renouvelle le jeton d'accès par le cookie. Une seule demande à la fois :
 * promesse partagée dans l'onglet, verrou entre onglets. Le verrou n'est rendu
 * qu'une fois la réponse lue, donc son `Set-Cookie` traité : l'onglet suivant
 * présente le cookie à jour, jamais celui qui vient d'être tourné.
 */
export function renouveler(): Promise<IssueRenouvellement> {
  if (!renouvellementEnCours) {
    verrou ??= verrouNavigateur();
    const enCours: Promise<IssueRenouvellement> = verrou
      .attendre(VERROU_RENOUVELLEMENT, renouvelerSousVerrou)
      .catch((): IssueRenouvellement => ({ etat: 'hors_ligne' }))
      .finally(() => {
        if (renouvellementEnCours === enCours) renouvellementEnCours = null;
      });
    renouvellementEnCours = enCours;
  }
  return renouvellementEnCours;
}

async function renouvelerSousVerrou(): Promise<IssueRenouvellement> {
  // Le cookie httpOnly survit à une déconnexion non reçue par le serveur : le
  // présenter rouvrirait la session du compte sorti.
  if (deconnexionEnAttente()) return { etat: 'refuse', message: MESSAGE_DECONNEXION_EN_ATTENTE, code: null };
  const avant = utilisateurId;
  const deconnexionsAvant = deconnexions;

  let statut: number;
  let corps: unknown;
  try {
    ({ statut, corps } = await echanger(
      `${BASE}/auth/refresh`,
      { method: 'POST', credentials: 'same-origin' },
      { delaiMs: DELAI_RENOUVELLEMENT_MS },
      async (reponse) => ({ statut: reponse.status, corps: await lireJson(reponse) }),
    ));
  } catch {
    return { etat: 'hors_ligne' };
  }

  if (deconnexions !== deconnexionsAvant) {
    return { etat: 'refuse', message: MESSAGE_DECONNEXION_EN_ATTENTE, code: null };
  }

  if (statut >= 200 && statut < 300 && reponseConnexionValide(corps)) {
    memoriserSession(corps);
    publier(ecouteursSession, { type: 'renouvelee', utilisateur: corps.utilisateur });
    if (avant !== null && avant !== corps.utilisateur.id) {
      publier(ecouteursSession, { type: 'autre_utilisateur', utilisateurId: corps.utilisateur.id });
    }
    return { etat: 'renouvele', reponse: corps };
  }

  if (statut === 401 || statut === 403) {
    const objet = typeof corps === 'object' && corps !== null ? (corps as Record<string, unknown>) : null;
    const message = objet ? messageDe(objet.message) : null;
    if (objet && message !== null) {
      // Refus jugé par l'API (session révoquée, expirée, compte inactif) : seul
      // cas où la session se perd. Les saisies du téléphone ne sont pas touchées ici.
      const code = typeof objet.code === 'string' ? objet.code : null;
      // COMPTE_INACTIF nomme le compte du cookie : au démarrage, l'onglet ne le connaît pas encore.
      const compte = typeof objet.utilisateurId === 'string' && objet.utilisateurId !== '' ? objet.utilisateurId : avant;
      memoriserSession(null);
      if (compte) sessionPerdue = { utilisateurId: compte, code };
      publier(ecouteursSession, { type: 'perdue', message, code, utilisateurId: compte });
      return { etat: 'refuse', message, code };
    }
  }
  // Portail Wi-Fi, passerelle, réponse illisible : le jeton en mémoire est gardé.
  return { etat: 'hors_ligne' };
}

async function fermerSessionServeur(): Promise<'faite' | 'en_attente'> {
  try {
    const statut = await echanger(
      `${BASE}/auth/logout`,
      { method: 'POST', credentials: 'same-origin' },
      { delaiMs: DELAI_DECONNEXION_MS },
      async (reponse) => {
        await reponse.text();
        return reponse.status;
      },
    );
    // 401 : le serveur ne connaît déjà plus la session, elle est bien fermée.
    if ((statut >= 200 && statut < 300) || statut === 401) {
      effacerMarqueDeconnexion();
      return 'faite';
    }
  } catch {
    /* réseau : la marque reste, la fermeture sera retentée */
  }
  return 'en_attente';
}

/**
 * Déconnexion : la marque est posée avant toute attente (un appel synchrone
 * suffit à la garantir), la session quitte la mémoire, puis le serveur est
 * prévenu. Deux demandes simultanées partagent le même envoi.
 */
export function deconnexion(): Promise<'faite' | 'en_attente'> {
  poserMarqueDeconnexion();
  deconnexions += 1;
  memoriserSession(null);
  oublierFiltres();
  oublierParcours();
  deconnexionVolontaire = true;
  sessionPerdue = null;
  if (!deconnexionEnCours) {
    const enCours = fermerSessionServeur().finally(() => {
      if (deconnexionEnCours === enCours) deconnexionEnCours = null;
    });
    deconnexionEnCours = enCours;
  }
  return deconnexionEnCours;
}

export function moi(): Promise<UtilisateurPublic> {
  return appel<UtilisateurPublic>('/auth/moi');
}

/** Style d'affichage du compte connecté (« Mon compte »). */
export function changerApparence(apparence: UtilisateurPublic['apparence']): Promise<UtilisateurPublic> {
  return appel<UtilisateurPublic>('/auth/apparence', { method: 'PUT', body: JSON.stringify({ apparence }) });
}

/**
 * Change le mot de passe du compte connecté. L'API ferme toutes ses sessions et
 * en rouvre une pour cet appareil : le nouveau jeton remplace l'ancien ici.
 */
export async function changerMotDePasse(actuel: string, nouveau: string): Promise<void> {
  const reponse = await appel<ReponseConnexion>('/auth/mot-de-passe', {
    method: 'POST',
    body: JSON.stringify({ actuel, nouveau }),
  });
  memoriserSession(reponse);
}
